const VIEWPORT_PADDING_PX = 8;
const PANEL_GAP_PX = 4;
const MIN_READABLE_WIDTH_PX = 280;
const MAX_PANEL_HEIGHT_PX = 240;
const OPEN_UP_THRESHOLD_PX = 160;

/**
 * Use the smallest reported viewport so Chrome device emulation cannot leak into DevTools.
 *
 * @param {{
 *   innerWidth: number,
 *   innerHeight: number,
 *   clientWidth: number,
 *   clientHeight: number,
 *   visualWidth?: number,
 *   visualHeight?: number,
 * }} measures
 * @returns {{ width: number, height: number }}
 */
export function getViewportBox(measures) {
  const width = Math.max(
    0,
    Math.min(
      measures.innerWidth,
      measures.clientWidth || measures.innerWidth,
      measures.visualWidth ?? measures.innerWidth,
    ),
  );
  const height = Math.max(
    0,
    Math.min(
      measures.innerHeight,
      measures.clientHeight || measures.innerHeight,
      measures.visualHeight ?? measures.innerHeight,
    ),
  );
  return { width, height };
}

/**
 * @param {{ top: number, left: number, bottom: number, width: number }} triggerRect
 * @param {{ width: number, height: number }} viewport
 * @returns {{ top: number, left: number, width: number, maxHeight: number }}
 */
export function getConstrainedSelectPanelStyle(triggerRect, viewport) {
  const availableWidth = Math.max(0, viewport.width - VIEWPORT_PADDING_PX * 2);
  const preferredWidth = Math.max(
    triggerRect.width,
    Math.min(MIN_READABLE_WIDTH_PX, availableWidth),
  );
  const width = Math.min(preferredWidth, availableWidth);

  const minLeft = VIEWPORT_PADDING_PX;
  const maxLeft = Math.max(minLeft, viewport.width - VIEWPORT_PADDING_PX - width);
  let left = Math.min(Math.max(triggerRect.left, minLeft), maxLeft);
  if (left + width > viewport.width - VIEWPORT_PADDING_PX) {
    left = Math.max(minLeft, viewport.width - VIEWPORT_PADDING_PX - width);
  }

  const spaceBelow = viewport.height - triggerRect.bottom - VIEWPORT_PADDING_PX;
  const spaceAbove = triggerRect.top - VIEWPORT_PADDING_PX;
  const openUp = spaceBelow < OPEN_UP_THRESHOLD_PX && spaceAbove > spaceBelow;
  const availableHeight = Math.max(0, (openUp ? spaceAbove : spaceBelow) - PANEL_GAP_PX);
  const maxHeight = Math.max(
    0,
    Math.min(MAX_PANEL_HEIGHT_PX, Math.floor(viewport.height * 0.45), availableHeight),
  );

  const top = openUp
    ? Math.max(VIEWPORT_PADDING_PX, triggerRect.top - PANEL_GAP_PX - maxHeight)
    : triggerRect.bottom + PANEL_GAP_PX;

  return { top, left, width, maxHeight };
}
